import { IndexedDBCryptoStore } from 'matrix-js-sdk/src/crypto/store/indexeddb-crypto-store';
import { LocalStorageCryptoStore } from 'matrix-js-sdk/src/crypto/store/localStorage-crypto-store';
import { MemoryCryptoStore } from 'matrix-js-sdk/src/crypto/store/memory-crypto-store';
import { MatrixClient } from 'matrix-js-sdk/src/matrix';
import { IndexedDBStore } from 'matrix-js-sdk/src/store/indexeddb';
import { MemoryStore } from 'matrix-js-sdk/src/store/memory';

import { storesLogger } from 'utils/logging/scopedLogger';

const indexedDB = global.indexedDB;
const localStorage = global.localStorage;
export const USER_NOT_MATCHING_MESSAGE = 'Matrix user does not match with the user in storage';
const DEVICE_ID_NO_MATCH_ERROR = "the account in the store doesn't match the account in the constructor";
export const KNOWN_MATRIX_AUTH_ERRORS = [USER_NOT_MATCHING_MESSAGE, DEVICE_ID_NO_MATCH_ERROR];

export const createMatrixStore = (): IndexedDBStore | MemoryStore | undefined => {
  if (indexedDB && localStorage) {
    return new IndexedDBStore({ indexedDB, localStorage, dbName: 'web-sync-store' });
  } else if (localStorage) {
    return new MemoryStore({ localStorage });
  } else {
    storesLogger.error('Failed to create a store in createIndexedDBStore');
  }
};

export const startupStores = async (matrixClient: MatrixClient | null) => {
  if (!matrixClient) return;

  for (const dbType of ['indexeddb', 'memory']) {
    try {
      const promise = matrixClient.store.startup();
      await promise;
      break;
    } catch (err) {
      if (dbType !== 'indexeddb') throw err;

      matrixClient.store = new MemoryStore({ localStorage });
    }
  }
};

export const createCryptoStore = () => {
  if (indexedDB) {
    return new IndexedDBCryptoStore(global.indexedDB, 'crypto-store');
  } else if (localStorage) {
    return new LocalStorageCryptoStore(localStorage);
  } else {
    return new MemoryCryptoStore();
  }
};

export enum MatrixLoggingInStatusEnum {
  INITIAL = 'initial',
  RUNNING = 'running',
  FINISHED = 'finished',
}

const CRYPTO_STORE_NAME = 'matrix-js-sdk::matrix-sdk-crypto';

/**
 * Tri-state result of probing the rust-crypto IndexedDB store on resume.
 * - `missing` — no DB, or the DB/object store exists but holds no `account` key. Normal for a fresh
 *   device mid-login or right after `clearStores()`. Must NOT trigger a hard logout.
 * - `valid` — the `account` key is present; crypto is usable.
 * - `corrupt` — the DB cannot be opened/read (open error, transaction error). Recovery (hard logout)
 *   is warranted.
 */
export type CryptoStoreStatus = 'missing' | 'valid' | 'corrupt';

/**
 * Probe the rust-crypto store without mutating it. Replaces the old boolean `isCryptoStoreValid`,
 * which collapsed `missing` and `corrupt` into `false` and caused returning SSO users on a fresh
 * device (empty store) to be hard-logged-out in a loop. Only `corrupt` should force recovery.
 */
export const getCryptoStoreStatus = (): Promise<CryptoStoreStatus> => {
  const objectStoreName = 'core';
  const accountKey = 'account';

  return new Promise((resolve) => {
    const request = indexedDB.open(CRYPTO_STORE_NAME);

    request.onerror = () => resolve('corrupt');

    // Fired when the DB did not exist before this open call — a fresh device, not corruption.
    request.onupgradeneeded = () => resolve('missing');

    request.onsuccess = (event) => {
      const db = (event.target as IDBRequest<IDBDatabase>).result;

      if (!db.objectStoreNames.contains(objectStoreName)) {
        db.close();
        return resolve('missing');
      }

      try {
        const transaction = db.transaction([objectStoreName], 'readonly');
        const store = transaction.objectStore(objectStoreName);
        const getRequest = store.get(accountKey);

        getRequest.onsuccess = () => {
          db.close();
          resolve(getRequest.result !== undefined ? 'valid' : 'missing');
        };

        getRequest.onerror = () => {
          db.close();
          resolve('corrupt');
        };
      } catch {
        db.close();
        resolve('corrupt');
      }
    };
  });
};
